import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import useApi from "../../../../utils/api";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { dateTimeFormat } from "../../../../utils/helpers/date";
import { getErrorMessage } from "../../../../utils/helpers/errors";
import "./style.scss";

const MINUS = "−";

export const formatSignedHours = (value) => {
    const number = Number(value) || 0;
    const fixed = Math.abs(number).toFixed(2);
    if (Number(fixed) === 0) return "0.00";
    return number > 0 ? `+${fixed}` : `${MINUS}${fixed}`;
};

// the small marker under a plan's balance; opens the details window
export function PendingAdjustmentBadge({ adjustment, onClick }) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            className="pending-adjustment-badge"
            onClick={onClick}
            title={t("pending-adjustment-review")}
        >
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
            {t("pending-adjustment-badge", { hours: formatSignedHours(adjustment.carry) })}
        </button>
    );
}

/**
 * Why a pending PTO adjustment waits, which pay periods it changes, who changed them and when,
 * and the balance after approval; Approve / Reject with an optional comment
 * (settings PtoPendingAdjustmentService).
 */
function PendingAdjustmentModal({ userId, planId, adjustmentId, isOpen, close, onDecided }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { authGet, authPost } = useApi();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(null);
    const [comment, setComment] = useState("");

    useEffect(() => {
        if (!isOpen || !userId || !planId || !adjustmentId) return;
        const controller = new AbortController();
        setDetails(null);
        setComment("");
        setLoading(true);
        authGet(`/time-off-plan/${userId}/adjustments/${planId}/${adjustmentId}`, { signal: controller.signal })
            .then((response) => setDetails(response || null))
            .catch((error) => {
                if (!controller.signal.aborted) getErrorMessage(error, t);
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [isOpen, userId, planId, adjustmentId, authGet, t]);

    const decide = useCallback(
        async (decision) => {
            setSubmitting(decision);
            try {
                await authPost(`/time-off-plan/${userId}/adjustments/${planId}/${adjustmentId}/${decision}`, {
                    data: decision === "reject" && comment ? { comment } : {},
                });
                toast.success(t(decision === "approve" ? "adjustment-approved" : "adjustment-rejected"));
                onDecided?.();
                close();
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setSubmitting(null);
            }
        },
        [authPost, userId, planId, adjustmentId, comment, onDecided, close, t],
    );

    const day = (value) => dateTimeFormat(value, dateFormat);
    const when = (value) => (value ? moment(value).format(`${dateFormat} HH:mm`) : "");
    const employeeName = details
        ? [details.employee?.firstName, details.employee?.lastName].filter(Boolean).join(" ") || details.employee?.employeeNumber
        : "";

    const reason = !details
        ? ""
        : details.kind === "grant"
          ? t("pending-adjustment-reason-grant", {
                plan: (details.fromPlanCode || "").replace(/\s*\(.*\)$/, ""),
                date: day(details.date),
                old: details.oldAmount,
                new: details.newAmount,
            })
          : details.kind === "recalculation"
          ? // fromPlanCode carries the date already ("HPTO (2025-10-13)"): the code alone here
            t("pending-adjustment-reason-recalculation", { plan: (details.fromPlanCode || "").replace(/\s*\(.*\)$/, ""), date: day(details.date) })
          : t("pending-adjustment-reason-transfer", {
                from: details.fromPlanCode || "",
                next: details.toPlanCode || details.planCode,
                to: details.planCode,
                date: day(details.date),
            });

    return (
        <Modal
            isOpen={isOpen}
            toggle={close}
            size="lg"
            scrollable
            className="pending-adjustment-modal"
            title={
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <span>{t("pending-adjustment-title")}</span>
                    <FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={close} />
                </div>
            }
            footer={
                details && (
                    <div className="d-flex justify-content-end gap-2 w-100">
                        <Button color="danger" outline size="sm" onClick={() => decide("reject")} loading={submitting === "reject"} disabled={!!submitting}>
                            {t("pending-adjustment-reject")}
                        </Button>
                        <Button color="success" size="sm" onClick={() => decide("approve")} loading={submitting === "approve"} disabled={!!submitting}>
                            {t("pending-adjustment-approve")}
                        </Button>
                    </div>
                )
            }
        >
            {loading && (
                <div className="d-flex justify-content-center py-4">
                    <div className="spinner spinner-border spinner-border-sm" role="status" />
                </div>
            )}

            {details && (
                <div className="pending-adjustment">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                        <div>
                            <h4 className="mb-0">{details.planCode}</h4>
                            <small className="text-muted">
                                {employeeName}
                                {details.employee?.employeeNumber ? ` · ${details.employee.employeeNumber}` : ""}
                            </small>
                        </div>
                        <span className={`pending-adjustment-amount ${details.carry < 0 ? "text-danger" : "text-green"}`}>
                            {t("pending-adjustment-hours", { hours: formatSignedHours(details.carry) })}
                        </span>
                    </div>

                    <div className="pending-adjustment-reason mb-3">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning mr-2" />
                        <span>{reason}</span>
                    </div>

                    {details.kind !== "grant" && (
                        <h5 className="text-uppercase text-muted text-xs mb-2">{t("pending-adjustment-periods")}</h5>
                    )}
                    {details.kind === "grant" ? null : details.periods?.length ? (
                        <div className="table-responsive mb-3">
                            <table className="table table-sm pending-adjustment-periods mb-0">
                                <thead>
                                    <tr>
                                        <th>{t("pending-adjustment-col-period")}</th>
                                        <th className="text-right">{t("pending-adjustment-col-accrued")}</th>
                                        <th className="text-right">{t("pending-adjustment-col-used")}</th>
                                        <th className="text-right">{t("pending-adjustment-col-lost")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.periods.map((period) => (
                                        <tr key={`${period.periodStartDate}-${period.periodEndDate}`}>
                                            <td>
                                                <div>{`${day(period.periodStartDate)} - ${day(period.periodEndDate)}`}</div>
                                                {period.events?.map((event, index) => (
                                                    <small key={index} className="d-block text-muted">
                                                        {t(event.type === "unlock" ? "pending-adjustment-event-unlock" : "pending-adjustment-event-lock", {
                                                            by: event.by || "?",
                                                            at: when(event.at),
                                                        })}
                                                    </small>
                                                ))}
                                            </td>
                                            <td className="text-right">{formatSignedHours(period.earned)}</td>
                                            <td className="text-right">{formatSignedHours(period.consumed)}</td>
                                            <td className="text-right">{formatSignedHours(period.lost)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-sm">{t("pending-adjustment-no-periods")}</p>
                    )}

                    <div className="pending-adjustment-balance mb-3">
                        <div>
                            <small className="text-muted d-block">{t("pending-adjustment-balance-now")}</small>
                            <strong>{Number(details.balanceNow).toFixed(2)}</strong>
                        </div>
                        <span className="pending-adjustment-arrow">→</span>
                        <div>
                            <small className="text-muted d-block">{t("pending-adjustment-balance-after")}</small>
                            <strong>{Number(details.balanceAfter).toFixed(2)}</strong>
                        </div>
                        {Number(details.lost) > 0 && (
                            <small className="text-muted ml-auto">{t("pending-adjustment-lost", { hours: Number(details.lost).toFixed(2) })}</small>
                        )}
                    </div>

                    {details.createdAt && (
                        <small className="text-muted d-block mb-3">
                            {t("pending-adjustment-created", {
                                by: [details.createdBy?.firstName, details.createdBy?.lastName].filter(Boolean).join(" ") || "?",
                                at: when(details.createdAt),
                            })}
                        </small>
                    )}

                    <label className="text-sm text-muted mb-1" htmlFor="pending-adjustment-comment">
                        {t("pending-adjustment-comment")}
                    </label>
                    <Input
                        id="pending-adjustment-comment"
                        type="textarea"
                        rows={2}
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                    />
                </div>
            )}
        </Modal>
    );
}

export default PendingAdjustmentModal;
